import React, { useMemo } from "react";
import { Order } from "src/shared/services/interfacev2";
import { splitDateTime } from "src/shared/components/dateTime";
import { useFormatAmount } from "src/business-types/laundry/modules/home/hooks/useFormatAmount";
import { useDateTimeFormatter } from "src/shared/configurations/hooks/useDateTimeFormatter";

interface OrderSummaryProps {
  order: Order;
}

/* ----------------------------------------------------------------------- */
/*  Presentational primitives - Consistent spacing & alignment             */
/* ----------------------------------------------------------------------- */

const InfoItem: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="flex flex-col gap-1">
    <span className="text-xs font-medium uppercase tracking-wider text-gray-400">{label}</span>
    <span className="text-sm font-semibold text-gray-900 truncate">{value}</span>
  </div>
);

const RowItem: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
    <span className="text-sm text-gray-500">{label}</span>
    <span className="text-sm font-medium text-gray-900 tabular-nums">{value}</span>
  </div>
);

const AmountCard: React.FC<{ label: string; value: React.ReactNode; variant?: 'success' | 'warning' }> = ({ 
  label, 
  value, 
  variant = 'success' 
}) => {
  const colorClasses = {
    success: 'bg-emerald-50 text-emerald-700',
    warning: 'bg-amber-50 text-amber-700'
  };

  return (
    <div className="flex items-center justify-between px-4 py-3 rounded-xl bg-gray-50">
      <span className="text-sm font-medium text-gray-600">{label}</span>
      <span className={`text-base font-bold tabular-nums ${colorClasses[variant]}`}>
        {value}
      </span>
    </div>
  );
};

const SectionDivider: React.FC = () => <div className="border-t border-gray-200 my-1" />;

/* ----------------------------------------------------------------------- */

const OrderSummary: React.FC<OrderSummaryProps> = ({ order }) => {
  const formatAmount = useFormatAmount();
  const { formatDate, formatTime } = useDateTimeFormatter();

  const receivedAmount = useMemo(() => {
    if (!order?.orders?.length) return 0;

    const received = order.orders.reduce((sum, o) => sum + Number(o.receivedAmount || 0) + Number(o.ledgerReceivedAmount || 0), 0);
    const finalAmount = order.orders.reduce((sum, o) => sum + Number(o.finalAmount || 0), 0);

    return Math.min(received, finalAmount);
  }, [order]);

  const remainingAmount = order?.orders?.length ? Math.max(Number(order.orders.at(-1)?.remainingAmount ?? 0), 0) : 0;
  const { date: orderDate, time: orderTime } = splitDateTime(order?.orderDetails[0]?.orderedOn?.$date ?? "");
  const customerName = [order?.customerDetails?.firstName, order?.customerDetails?.middleName, order?.customerDetails?.lastName].filter(Boolean).join(" ");
  const subtotal = (Number(order?.orders[0]?.finalAmount || 0) + Number(order?.orders[0]?.discount || 0)).toFixed(2);
  const discount = order?.orders[0]?.discount || 0;
  const totalAmount = order?.orders[0]?.finalAmount || "0";
  const paymentMethod = order?.paymentHistory[0]?.paymentMethod || "N/A";
  const staff = order?.orders?.[0]?.staffDetails;
  const staffName = [staff?.firstName, staff?.middleName, staff?.lastName].filter(Boolean).join(" ") || "N/A";

  return (
    <div className="h-full flex flex-col bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Scrollable Content with consistent padding */}
      <div className="flex-1 overflow-y-auto px-4 py-3">
        <div className="flex flex-col gap-3">
          {/* Customer & Staff Info */}
          <div className="grid grid-cols-2 gap-3">
            <InfoItem label="Customer" value={customerName || "N/A"} />
            <InfoItem label="Staff" value={staffName} />
          </div>

          <SectionDivider />

          {/* Financial Details - Clean rows */}
          <div className="space-y-0">
            <RowItem label="Subtotal" value={`${subtotal} QAR`} />
            <RowItem label="Discount" value={`${formatAmount(discount)} QAR`} />
            <RowItem label="Total" value={`${formatAmount(totalAmount)} QAR`} />
            <RowItem label="Payment" value={<span className="capitalize">{paymentMethod}</span>} />
            <RowItem 
              label="Order Date" 
              value={`${formatDate(orderDate)} · ${formatTime(orderTime)}`} 
            />
          </div>

          <SectionDivider />

          {/* Amount Cards - Clear visual hierarchy */}
          <div className="space-y-1">
            <AmountCard 
              label="Received" 
              value={`${formatAmount(receivedAmount)} QAR`} 
              variant="success"
            />
            <AmountCard 
              label="Remaining" 
              value={`${formatAmount(remainingAmount)} QAR`} 
              variant={remainingAmount > 0 ? "warning" : "success"}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderSummary;